// CORE IMPORTS
import React from "react";
import { capitalize } from "lodash";

// MATERIAL UI
import { styled } from "@mui/system";
import { Link as RouterLink } from "react-router-dom";

// COMPONENTS
import { formatPrice } from "src/components/portfolio/helpers";
import { LoadingSpinner } from "src/components/application/LoadingSpinner";

// REDUX
import api from "src/store/features/api";
import { useUserSlice } from "src/store/features/user.slice";
import { useSwapSlice } from "src/store/features/swap.slice";

// UTILS
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";

export const Wrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  height: "100%",
  padding: "24px",
  gap: "24px",
  borderRadius: "20px",
  background: theme.palette.primary.main,
}));

export const Header = styled("div")(({ theme }) => ({
  alignSelf: "stretch",
  color: theme.palette.common.white,
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 600,
}));

export const LoadingWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100%",
}));

export const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  flex: "1 0 0",
  alignSelf: "stretch",
}));

export const ListWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  height: "173px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
}));

export const ListHeader = styled("div")(({ theme }) => ({
  alignSelf: "stretch",
  paddingBottom: "8px",
  color: theme.palette.common.white,
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "20px",
  letterSpacing: "-0.32px",
  borderBottom: "1px solid #CCC3AE",
}));

export const ListSubtitle = styled("div")(({ theme }) => ({
  alignSelf: "stretch",
  color: "#CCC3AE",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
}));

export const CTWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: "4px",
  alignSelf: "stretch",
}));

export const CTHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
}));

interface CTHeaderTextProps {
  weight?: number;
  size?: string;
  lineHeight?: string;
  letterSpacing?: string;
  flex?: string;
}
export const CTHeaderText = styled("div")<CTHeaderTextProps>(
  ({ weight, size, lineHeight, letterSpacing, flex, theme }) => ({
    color: theme.palette.common.white,
    flex: flex ?? "",
    fontFamily: "Roobert",
    fontSize: size ?? "16px",
    fontStyle: "normal",
    fontWeight: weight ?? 600,
    lineHeight: lineHeight ?? "20px",
    letterSpacing: letterSpacing ?? "-0.32px",
  }),
);

export const SubHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
}));

interface SubHeaderTextProps {
  flex?: string;
}
export const SubHeaderText = styled("div")<SubHeaderTextProps>(({ flex }) => ({
  color: "#CCC3AE",
  flex: flex ?? "",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
}));

export const LinkWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "12px 16px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "4px",
}));

export const SeeAllButton = styled(RouterLink)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  fontFamily: "Roobert",
  fontSize: "18px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "22px",
}));

interface CTListItemProps {
  header: string;
  status: string;
  sell: string;
  buy: string;
  total: string;
}

const RecentTradesV2 = () => {
  const { defaultCurrency } = useUserSlice();
  const { code: userBaseCurrency } = defaultCurrency;
  const { fetchRecentTrades } = useSwapSlice();

  const {
    data: recentTrades = [],
    isLoading,
    refetch: refetchCTS,
  } = api.useGetCreatedCtQuery(undefined, {
    pollingInterval: 60000,
  });

  React.useEffect(() => {
    if (fetchRecentTrades) {
      refetchCTS();
    }
  }, [fetchRecentTrades]);

  // Function to separate trades by status
  const separateTradesByStatus = (trades: any) => {
    // Initialize the accumulator with empty arrays for requested and completed
    const result = { requested: [], completed: [] };

    // Check if trades array is empty
    if (trades.length <= 0) return result;

    // Separate trades by status
    return trades.reduce((acc: any, trade: any) => {
      const { status } = trade;
      const key = status.includes("Completed") ? "completed" : "requested";
      acc[key].push(trade);
      return acc;
    }, result);
  };

  const separatedTrades = React.useMemo(() => separateTradesByStatus(recentTrades), [recentTrades]);

  const CTListItem = (props: CTListItemProps) => {
    const { header, status, sell, buy, total } = props;

    let newStatus: string = "";
    switch (status) {
      case "Trade Order Requested":
        newStatus = "Requested";
        break;
      case "Trade Order Pending":
        newStatus = "Pending";
        break;
      case "Trade Order Processing":
        newStatus = "Processing";
        break;
      case "Trade Order Completed":
        newStatus = "Completed";
        break;
      default:
        newStatus = status;
        break;
    }

    return (
      <CTWrapper>
        <CTHeader>
          <CTHeaderText flex="1">{header}</CTHeaderText>
          <CTHeaderText size="14px" weight={400} lineHeight="16px" letterSpacing="">
            {capitalize(newStatus)}
          </CTHeaderText>
        </CTHeader>
        <SubHeader>
          <SubHeaderText flex="1">{`${sell} -> ${buy}`}</SubHeaderText>
          <SubHeaderText>{total}</SubHeaderText>
        </SubHeader>
      </CTWrapper>
    );
  };

  const InProgressHeader = separatedTrades.requested.length > 0 ? "Open" : "In Progress";

  return (
    <Wrapper>
      <Header>Recent Swaps</Header>
      {isLoading ? (
        <LoadingWrapper>
          <LoadingSpinner size={40} />
        </LoadingWrapper>
      ) : (
        <ContentWrapper>
          <ListWrapper>
            <ListHeader>{InProgressHeader}</ListHeader>
            {separatedTrades.requested.length === 0 ? (
              <ListSubtitle>You currently have no in progress orders</ListSubtitle>
            ) : (
              separatedTrades.requested.slice(0, 3).map((trade: any, index: number) => {
                const sell = trade.lines.find((line: any) => line.side === "Sell");
                const buy = trade.lines.find((line: any) => line.side === "Buy");
                return (
                  <CTListItem
                    key={index}
                    header={dateFormatter(trade.created, false, true)}
                    status={trade.status}
                    sell={sell.currencyConfig.code}
                    buy={buy.currencyConfig.code}
                    total={formatPrice(trade.value, userBaseCurrency)}
                  />
                );
              })
            )}
          </ListWrapper>
          <ListWrapper>
            <ListHeader>Completed</ListHeader>
            {separatedTrades.completed.length === 0 ? (
              <ListSubtitle>You currently have no completed orders</ListSubtitle>
            ) : (
              separatedTrades.completed.slice(0, 3).map((trade: any, index: number) => {
                const sell = trade.lines.find((line: any) => line.side === "Sell");
                const buy = trade.lines.find((line: any) => line.side === "Buy");
                return (
                  <CTListItem
                    key={index}
                    header={dateFormatter(trade.completed, false, true)}
                    status={trade.status}
                    sell={sell.currencyConfig.code}
                    buy={buy.currencyConfig.code}
                    total={formatPrice(trade.value, userBaseCurrency)}
                  />
                );
              })
            )}
          </ListWrapper>
        </ContentWrapper>
      )}
      <LinkWrapper>
        <SeeAllButton to="/transactions/overview">See All</SeeAllButton>
      </LinkWrapper>
    </Wrapper>
  );
};

export default RecentTradesV2;
